import { round } from 'lodash';

import IconTooltip from '../../../ui/IconTooltip/IconTooltip';

/**
 * Render the completion percentage cell
 * @param value
 * @param returnPercentage - If true, return 0% if value is undefined, else return N/A
 * @returns The completion percentage cell
 */
export const renderPercentageCell = (
  value: Optional<number>,
  returnPercentage: boolean = false,
) => {
  if (value || value === 0) return <>{`${value}%`}</>;

  if (returnPercentage) return '0%';

  return (
    <span>
      N/A
      <IconTooltip
        id="tooltip-na"
        icon="help"
        title="Completion Percentage N/A"
        text="This course is not assigned to this learner (from the selected teams) and does not contribute to their total completion calculation."
      />
    </span>
  );
};

/**
 * Get the total percentage (weighted average) complete for all active courses
 * This function adds up the total slides completed and total slides count for all active courses
 * and returns the percentage complete by dividing the total slides completed by the total slides count
 * @param courseProgresses - The course progresses for a learner
 * @param activeCourses - The active courses
 * @returns The total percentage complete
 * */
export const getTotalPercentageComplete = (
  courseProgresses: Optional<IReportCourseProgress[]>,
  activeCourses: string[],
) => {
  const workingCourses = courseProgresses?.filter((course) => activeCourses.includes(course._id)); // Filter out course that the learner is not assigned to
  const defaultProgress = { slidesCompleted: 0, slidesCount: 0 };

  const { slidesCompleted, slidesCount } =
    workingCourses?.reduce(
      (acc: { slidesCompleted: number; slidesCount: number }, course: IReportCourseProgress) => {
        if (activeCourses.includes(course._id)) {
          acc.slidesCompleted += course.slidesCompleted || 0;
          acc.slidesCount += course.slidesCount || 0;
        }

        return acc;
      },
      defaultProgress,
    ) || defaultProgress;

  return slidesCount ? round((slidesCompleted / slidesCount) * 100, 2) : 0;
};

/**
 * Get the total percentage complete for each course
 * @param courseProgresses - The course progresses for a learner
 * @returns The total percentage complete for each course
 * */
export const getTotalPercentageCompleteForEachCourse = (
  courseProgresses: Optional<IReportCourseProgress[]>,
) => {
  return courseProgresses?.reduce((acc: any, course: IReportCourseProgress) => {
    acc[course._id] = round(course.progress || 0, 2);
    return acc;
  }, {});
};
